import {useMemo, useEffect, useState} from 'react'
import {generateMonthLabels, processChartData} from './utils'
import {getChartOptions, getChartDataConfig} from './chartConfig'
import {EnhancementReportStat} from '../../../../models/enhancement/enhancement-report-stats'
import {EnhancementReportSearch} from '../../hooks/useEnhancementsFilter'
import {ChartWrapper, useChart, useChartError} from '../common'
import {PeriodType} from '../../PeriodTypeSelector'
import {generatePayPeriodLabelsFromData, getChartTitle} from '../../../../utils/payPeriodUtils'

interface EnhancementsHoursByTypeChartProps {
  readonly reportData: EnhancementReportStat[]
  readonly filterData: EnhancementReportSearch
  readonly requestTypes: string[] | undefined
  readonly isLoading: boolean
  readonly periodType: PeriodType
}

function EnhancementsHoursByTypeChart({reportData, isLoading, filterData, requestTypes, periodType}: EnhancementsHoursByTypeChartProps) {
  const chartRef = useChart()
  const {error, handleError, setError} = useChartError()

  const [chartData, setChartData] = useState<{
    labels: string[]
    processedData: any
  }>({
    labels: [],
    processedData: null
  })

  const chartOptions = useMemo(() => getChartOptions(chartData.labels, periodType), [chartData.labels, periodType])

  const chartDataConfig = useMemo(() => {
    if (!chartData.processedData) return null
    return getChartDataConfig(chartData.labels, chartData.processedData)
  }, [chartData.labels, chartData.processedData])

  useEffect(() => {
    try {
      if (!reportData || reportData.length === 0) {
        setChartData({ labels: [], processedData: null })
        return
      }

      let labels: string[]
      if (periodType === PeriodType.PAY_PERIOD) {
        labels = generatePayPeriodLabelsFromData(reportData)
      } else {
        labels = generateMonthLabels(filterData.dateFrom, filterData.dateTo)
      }

      const processedData = processChartData(
        reportData,
        filterData.dateFrom,
        filterData.dateTo,
        requestTypes,
        periodType
      )

      setChartData({ labels, processedData })
      setError(null)
    } catch (err) {
      handleError(err, 'processing chart data')
    }
  }, [reportData, periodType, requestTypes, handleError, setError])

  const hasData = Boolean(reportData?.length && chartDataConfig)

  return (
    <ChartWrapper
      ref={chartRef}
      title={getChartTitle(periodType)}
      error={error}
      isLoading={isLoading}
      hasData={hasData}
      chartType="line"
      chartData={chartDataConfig || undefined}
      chartOptions={chartOptions}
      height={500}
    />
  )
}

export default EnhancementsHoursByTypeChart